import React, {createContext, PropsWithChildren, useCallback, useContext, useMemo} from 'react';

import {
  CandidateSearchParams,
  CandidateSearchResponse,
  EmployerCandidateService,
  EmployerCandidateServiceImpl,
  ListSavedCandidatesParams,
  SaveCandidateParams,
  SavedCandidateListResponse,
  UpdateSavedCandidateParams,
} from '../services';
import {SavedCandidate} from '../models';
import {useAuth} from './auth.provider';

interface EmployerCandidateProviderValue {
  listSavedCandidates: (params?: ListSavedCandidatesParams) => Promise<SavedCandidateListResponse>;
  removeSavedCandidate: (savedCandidateId: string) => Promise<void>;
  saveCandidate: (params: SaveCandidateParams) => Promise<SavedCandidate>;
  searchCandidates: (params: CandidateSearchParams) => Promise<CandidateSearchResponse>;
  updateSavedCandidate: (params: UpdateSavedCandidateParams) => Promise<SavedCandidate>;
}

const EmployerCandidateContext = createContext<EmployerCandidateProviderValue | null>(null);

export const useEmployerCandidate = (): EmployerCandidateProviderValue => {
  const context = useContext(EmployerCandidateContext);
  if (!context) {
    throw new Error('useEmployerCandidate must be used within an EmployerCandidateProvider');
  }
  return context;
};

export const useOptionalEmployerCandidate = (): EmployerCandidateProviderValue | null => {
  return useContext(EmployerCandidateContext);
};

export const EmployerCandidateProvider: React.FC<PropsWithChildren> = ({children}) => {
  const employerCandidateService: EmployerCandidateService = useMemo(() => new EmployerCandidateServiceImpl(), []);
  const {user} = useAuth();

  const ensureAuthenticated = useCallback(() => {
    if (!user?.id) {
      throw new Error('Please sign in again to continue.');
    }
  }, [user?.id]);

  const searchCandidates = useCallback(
    async (params: CandidateSearchParams): Promise<CandidateSearchResponse> => {
      ensureAuthenticated();
      const response = await employerCandidateService.searchCandidates(params);
      if (response.hasError() || !response.data) {
        throw new Error(response.error || 'Unable to fetch candidates right now. Please try again.');
      }
      return response.data;
    },
    [employerCandidateService, ensureAuthenticated],
  );

  const listSavedCandidates = useCallback(
    async (params?: ListSavedCandidatesParams): Promise<SavedCandidateListResponse> => {
      ensureAuthenticated();
      const response = await employerCandidateService.listSavedCandidates(params);
      if (response.hasError() || !response.data) {
        throw new Error(response.error || 'Unable to load saved candidates.');
      }
      return response.data;
    },
    [employerCandidateService, ensureAuthenticated],
  );

  const saveCandidate = useCallback(
    async (params: SaveCandidateParams): Promise<SavedCandidate> => {
      ensureAuthenticated();
      const response = await employerCandidateService.saveCandidate(params);
      if (response.hasError() || !response.data) {
        throw new Error(response.error || 'Unable to save candidate.');
      }
      return response.data;
    },
    [employerCandidateService, ensureAuthenticated],
  );

  const updateSavedCandidate = useCallback(
    async (params: UpdateSavedCandidateParams): Promise<SavedCandidate> => {
      ensureAuthenticated();
      const response = await employerCandidateService.updateSavedCandidate(params);
      if (response.hasError() || !response.data) {
        throw new Error(response.error || 'Unable to update saved candidate.');
      }
      return response.data;
    },
    [employerCandidateService, ensureAuthenticated],
  );

  const removeSavedCandidate = useCallback(
    async (savedCandidateId: string): Promise<void> => {
      ensureAuthenticated();
      const response = await employerCandidateService.removeSavedCandidate(savedCandidateId);
      if (response.hasError()) {
        throw new Error(response.error || 'Unable to remove saved candidate.');
      }
    },
    [employerCandidateService, ensureAuthenticated],
  );

  return (
    <EmployerCandidateContext.Provider
      value={{listSavedCandidates, removeSavedCandidate, saveCandidate, searchCandidates, updateSavedCandidate}}>
      {children}
    </EmployerCandidateContext.Provider>
  );
};
